/**
 * 修改密码弹窗（Header 管理员菜单与个人设置页共用）；校验规则沿用旧版 Header.vue
 */
import { useState } from 'react'
import { App, Form, Input, Modal } from 'antd'
import { useAuthStore } from '@/stores/auth'

interface PasswordForm {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

export function ChangePasswordForm({ form }: Readonly<{ form: ReturnType<typeof Form.useForm<PasswordForm>>[0] }>) {
  return (
    <Form form={form} layout="vertical" preserve={false}>
      <Form.Item label="当前密码" name="oldPassword" rules={[{ required: true, message: '请输入当前密码' }]}>
        <Input.Password placeholder="请输入当前密码" autoComplete="current-password" />
      </Form.Item>
      <Form.Item
        label="新密码"
        name="newPassword"
        rules={[
          { required: true, message: '请输入新密码' },
          { min: 8, message: '密码长度不能少于8位' },
          {
            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
            message: '密码需包含大小写字母、数字和特殊字符'
          }
        ]}
      >
        <Input.Password placeholder="请输入新密码" autoComplete="new-password" />
      </Form.Item>
      <Form.Item
        label="确认新密码"
        name="confirmPassword"
        dependencies={['newPassword']}
        rules={[
          { required: true, message: '请确认新密码' },
          ({ getFieldValue }) => ({
            validator: (_, value) =>
              !value || value === getFieldValue('newPassword')
                ? Promise.resolve()
                : Promise.reject(new Error('两次输入的密码不一致'))
          })
        ]}
      >
        <Input.Password placeholder="请再次输入新密码" autoComplete="new-password" />
      </Form.Item>
    </Form>
  )
}

// 校验并提交，成功返回 true
export function useSubmitPasswordChange(form: ReturnType<typeof Form.useForm<PasswordForm>>[0]) {
  const { message } = App.useApp()
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    // 校验不通过时表单会显示错误，这里直接返回
    const values = await form.validateFields().catch(() => null)
    if (!values) return false
    setSubmitting(true)
    try {
      const success = await useAuthStore.getState().changePassword(values.oldPassword, values.newPassword)
      if (success) {
        message.success('密码修改成功')
        form.resetFields()
      }
      return success
    } finally {
      setSubmitting(false)
    }
  }
  return { submit, submitting }
}

export function ChangePasswordModal({ open, onClose }: Readonly<{ open: boolean; onClose: () => void }>) {
  const [form] = Form.useForm<PasswordForm>()
  const { submit, submitting } = useSubmitPasswordChange(form)

  return (
    <Modal
      title="修改密码"
      open={open}
      confirmLoading={submitting}
      destroyOnHidden
      onCancel={onClose}
      onOk={async () => {
        if (await submit()) onClose()
      }}
    >
      <ChangePasswordForm form={form} />
    </Modal>
  )
}
